/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { removeBreadcrumb } from 'redux/actions/layoutActions';
import { setFoldersCount } from 'redux/actions/resultActions';
import {
  getDocumentPath,
  getReadDocument,
  refreshPath,
  setNavigationPane,
} from 'redux/actions/documentActions';
import { selectCurrentClient } from 'redux/actions/clientActions';

import { L10n, createElement, setCulture } from '@syncfusion/ej2-base';
import { TextBox } from '@syncfusion/ej2-react-inputs';
import { TextField } from 'shared/components/form/Inputs';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { colors } from 'shared/defaultTheme';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Inject,
  Filter,
  Resize,
} from '@syncfusion/ej2-react-grids';
import useApi from 'hooks/useApi';
import { MultiSelect, CheckBoxSelection } from '@syncfusion/ej2-react-dropdowns';
import { capitalizeFirstLetter } from 'shared/utils';
import { DataUtil } from '@syncfusion/ej2-data';
import PropTypes from 'prop-types';
import { DocumentationResultsWrapper } from '../styles';
import { L10nObj } from '../contants';

DataUtil.serverTimezoneOffset = 2 * (new Date().getTimezoneOffset() / 60);
MultiSelect.Inject(CheckBoxSelection);

const FolderResults = forwardRef(({ getCounters }, ref) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { generalSearchText } = useSelector((state) => state.results);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState();
  const { isCustomer, cusNavId, mail, clientId } = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [language, setLanguage] = useState('es');
  const { customAreaApi } = useApi();
  const [searchValue, setSearchValue] = useState('');
  const [pageSettings, setPageSettings] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const loadingIndicator = { indicatorType: 'Shimmer' };
  const [isPublicOptions, setIsPublicOptions] = useState(null);
  const grid = useRef(null);

  let data;
  let dropInstance;

  useEffect(() => {
    L10n.load(L10nObj);
    const lang = selectedDefaultLanguage.slice(0, 2);
    setLanguage(lang);
    setCulture(lang);
    setIsPublicOptions([
      { id: true, text: t('general_button_yes') },
      { id: false, text: t('general_button_no') },
    ]);
  }, [selectedDefaultLanguage]);

  const gridIsPublicTemplate = (args) => {
    const { readers } = args;
    const isPublic = !!(readers && readers.length && readers.length > 0);
    return (
      <div
        className={classNames('public-wrapper', {
          'public-yes': isPublic,
          'public-no': !isPublic,
        })}
      >
        {isPublic ? t('general_button_yes') : t('general_button_no')}
      </div>
    );
  };

  const [, getFolders] = customAreaApi(
    {
      url: `${window.API_V1}/cli/items/${cusNavId}/${encodeURIComponent(mail)}/search/all`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getFoldersCount] = customAreaApi(
    {
      url: `${window.API_V1}/cli/items/${cusNavId}/${encodeURIComponent(mail)}/search/all/count`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getClient] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const setLoader = (value) => {
    if (!document.querySelector('.e-spinner-pane')) return;
    if (value) {
      document.querySelector('.e-spinner-pane').style.display = 'block';
    } else {
      document.querySelector('.e-spinner-pane').style.display = 'none';
    }
  };

  const getFilters = (state) => {
    const filtering = state?.where?.[0].predicates || [];
    const finalFilters = [];
    filtering.forEach((x) => {
      if (x.field === 'isPublic') {
        const isPublicNames = x.value.split(',');
        const isPublicValues = isPublicOptions
          .filter((s) => isPublicNames.includes(s.text))
          .map((s) => s.id);

        if (isPublicValues.length === 2) return;

        isPublicValues.forEach((value) => {
          const filter = { ...x, value };
          finalFilters.push(filter);
        });
      } else {
        finalFilters.push(x);
      }
    });
    return finalFilters;
  };

  const getData = async (state) => {
    setLoader(true);
    let filtering;
    let sortingFilter = sorting;

    if (state?.action?.action === 'filter' || state?.action?.action === 'clearFilter') {
      filtering = getFilters(state);
      setFilters(filtering);
    }
    const currentFilters = filtering || filters;
    const dataFilters = currentFilters.map((x) => {
      if (
        x.predicates &&
        x.predicates.length > 1 &&
        (x.predicates[0].field === 'created' || x.predicates[0].field === 'modified')
      ) {
        return {
          model: 'PathItem',
          field: x.predicates[0].field.charAt(0).toUpperCase() + x.predicates[0].field.slice(1),
          filter: [x.predicates[0].value, x.predicates[1].value],
          isGreaterThan: true,
        };
      }

      if (x.field === 'isPublic') {
        return {
          model: 'PathItem',
          field: 'IsPublic',
          filter: [!!x.value],
        };
      }

      let filter = {
        model: 'PathItem',
        field: x.field.charAt(0).toUpperCase() + x.field.slice(1),
        filter: [x.value],
        IsRegexDiacritics: true,
      };
      if (x?.operator === 'greaterthan') {
        filter = { ...filter, isGreaterThan: true };
      }
      if (x?.operator === 'lessthan') {
        filter = { ...filter, isLessThan: true };
      }
      if (x?.operator === 'contains') {
        filter = { ...filter, IsRegexDiacritics: true };
      }
      return filter;
    });

    if (state?.action?.requestType === 'sorting' && state?.action?.columnName) {
      let model = 'PathItem';

      if (state?.action?.columnName === 'clientName') {
        model = 'Client';
      }

      sortingFilter = {
        model,
        field: capitalizeFirstLetter(state?.action?.columnName),
        asc: state?.action?.direction === 'Ascending',
      };
    }

    if (state?.action?.requestType === 'sorting' && !state?.action?.columnName) {
      sortingFilter = null;
    }

    setSorting(sortingFilter);

    dataFilters.push(
      sortingFilter || {
        model: 'PathItem',
        field: 'Created',
        asc: false,
      }
    );

    dataFilters.push({
      model: 'PathItem',
      field: 'IsFile',
      filter: [false],
    });

    if (!isCustomer) {
      dataFilters.push({
        model: 'PathItem',
        field: 'ClientId',
        filter: [clientId],
      });
    }

    const body = {
      filters: dataFilters,
      reduce: {
        limit: state.take,
        skip: state.skip / pageSize + 1,
      },
      ...((searchValue || generalSearchText) && {
        filterText: {
          regexFilter: searchValue || generalSearchText,
        },
      }),
    };

    try {
      const {
        data: { data: count },
      } = await getFoldersCount({
        data: body,
      });

      const {
        data: { data: folders },
      } = await getFolders({
        data: body,
      });

      dispatch(setFoldersCount(count));
      setLoader(false);

      const foldersData = folders?.map((e) => ({
        ...e,
      }));

      return {
        count: count || 0,
        result: foldersData || [],
      };
    } catch (error) {
      console.error(`Error Contacts: ${error}`);
      return {
        count: 0,
        result: [],
      };
    }
  };

  const dataStateChange = async (state) => {
    setPageSize(state.take);
    const requestType = state?.action?.requestType;
    if (requestType === 'stringfilterrequest') return;
    const dataSource = await getData(state);
    if (grid.current) {
      grid.current.dataSource = dataSource;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const state = { skip: 0, take: pageSize };
      await dataStateChange(state);
    };
    setPageSettings({ pageCount: 3, pageSize, pageSizes: [5, 10, 20, 50, 75, 100] });
    fetchData();
  }, []);

  const getFilter = (field) => {
    const filter = filters.find((x) => x.field === field);
    return filter?.value || '';
  };

  const customFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new TextBox({
          type: 'text',
          placeholder: t('results_filter_search_value'),
          value: getFilter(args.column.field),
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        grid.current.removeFilteredColsByField(args.column.field);
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.value);
      },
      write: (args) => {
        if (args.filteredValue) {
          dropInstance.value = args.filteredValue;
        }
      },
    },
  };

  const isPublicFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new MultiSelect({
          dataSource: isPublicOptions,
          fields: { text: 'text', value: 'id' },
          placeholder: t('results_filter_search_value'),
          mode: 'CheckBox',
          filtering: (e) => {
            const query = new RegExp(e.text, 'i');
            e.updateData(isPublicOptions.filter((x) => query.test(x.text)));
          },
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.text);
      },
      write: (args) => {
        dropInstance.text = args.filteredValue || '';
      },
    },
  };

  useImperativeHandle(ref, () => ({
    async doSearch() {
      const state = { skip: 0, take: pageSize };
      await getCounters();
      await dataStateChange(state);
    },
  }));

  const handleSearch = (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
    } else {
      setSearchValue('');
    }
  };

  const getClientById = async (id) => {
    try {
      const { data: clientData } = await getClient({
        url: `${window.API_V1}/cli/clients/${cusNavId}/${id}`,
      });
      return clientData?.data;
    } catch (error) {
      console.error(`Error getting client: ${error}`);
      return error;
    }
  };

  const handleRowSelected = async (row) => {
    const { data: currentDocument } = row;
    localStorage.removeItem('documentFormState');
    localStorage.removeItem('localizacionPath');
    localStorage.removeItem('setPath');
    const findClient = await getClientById(currentDocument.clientId);
    dispatch(selectCurrentClient(findClient));
    currentDocument.tags = currentDocument?.tags || [];
    localStorage.setItem('documentFormState', JSON.stringify(currentDocument));
    localStorage.setItem('setPath', currentDocument.pathId);
    dispatch(getDocumentPath(currentDocument.pathId));
    dispatch(getReadDocument(null));
    dispatch(removeBreadcrumb());
    dispatch(refreshPath(true));
    dispatch(setNavigationPane(true));
    history.push('/documentations');
  };

  const onSearch = async () => {
    const state = { skip: 0, take: pageSize };
    await dataStateChange(state);
  };

  const onInputChange = async (e) => {
    if (e.key === 'Enter') {
      await onSearch();
    }
  };

  useEffect(() => {
    (async () => {
      if (searchValue === '') {
        const state = { skip: 0, take: pageSize };
        await dataStateChange(state);
      }
    })();
  }, [searchValue]);

  return (
    <DocumentationResultsWrapper>
      <div className="filter-container">
        <div className="form flex-column" noValidate>
          <div className="d-flex flex-row align-items-center h-50 ml-filter-section selector-filter-by-wrapper" />
          <TextField
            borderRadius={2}
            className="mb-4 m-0 h-50 filter-input"
            inputClassName="w-100"
            withButtonClear
            rightIcon={
              <SearchIcon fill={colors.black} onClick={onSearch} style={{ cursor: 'pointer' }} />
            }
            placeholder={t('results_search_placeholder_folder')}
            value={searchValue ?? ''}
            onChange={handleSearch}
            onKeyDown={onInputChange}
          />
        </div>
      </div>

      <div className="control-pane">
        <div className="control-section">
          <GridComponent
            id="Grid"
            dataSource={data}
            ref={(g) => {
              grid.current = g;
            }}
            allowPaging
            pageSettings={pageSettings}
            dataStateChange={dataStateChange}
            allowSorting
            allowFiltering
            filterSettings={{
              type: 'Menu',
              ignoreAccent: true,
              operators: {
                dateOperator: [
                  { value: 'equal', text: t('filters.equal') },
                  { value: 'greaterthan', text: t('filters.greaterthan') },
                  { value: 'lessthan', text: t('filters.lessthan') },
                ],
              },
            }}
            locale={language}
            rowSelected={(args) => {
              handleRowSelected(args);
            }}
            loadingIndicator={loadingIndicator}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="clientName"
                headerTemplate={t('general_client')}
                minWidth="200"
                width="auto"
                textAlign="Left"
                filter={customFilter}
              />
              <ColumnDirective
                field="name"
                headerText={t('panel_table_name')}
                minWidth="200"
                width="auto"
                textAlign="Left"
                filter={customFilter}
              />
              <ColumnDirective
                field="created"
                headerText={t('panel_table_height')}
                width="160"
                textAlign="Left"
                type="date"
                format={{ type: 'date', format: 'dd/MM/yyyy HH:mm' }}
              />
              <ColumnDirective
                field="modified"
                headerText={t('panel_table_date')}
                width="180"
                textAlign="Left"
                type="date"
                format={{ type: 'date', format: 'dd/MM/yyyy HH:mm' }}
              />
              <ColumnDirective
                field="isPublic"
                headerText={t('panel_table_public')}
                width="120"
                textAlign="Left"
                template={gridIsPublicTemplate}
                filter={isPublicFilter}
              />
            </ColumnsDirective>
            <Inject services={[Page, Filter, Resize]} />
          </GridComponent>
        </div>
        <div id="waitingpopup" className="waitingpopup">
          <span id="gif" className="image" />
        </div>
      </div>
    </DocumentationResultsWrapper>
  );
});

FolderResults.propTypes = {
  getCounters: PropTypes.func.isRequired,
};

export default FolderResults;
